/**
 * Web Push (VAPID) helpers for the admin dashboard.
 * Uses the service worker already registered by public/index.html.
 *
 * IMPORTANT: Notification.requestPermission() MUST be called from the
 * synchronous prelude of a user gesture. Any awaited work before it may
 * cause modern browsers (Chrome/Android in particular) to silently
 * refuse to show the permission popup. We therefore split the flow:
 *   1. requestNotificationPermission() — call FIRST in the click handler.
 *   2. registerPushSubscription()      — call after, once permission is granted.
 */
import API from "@/lib/api";

const SW_PATH = "/sw.js";

export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        window.isSecureContext &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export function isInIframe() {
    try {
        return typeof window !== "undefined" && window.top !== window.self;
    } catch {
        return true;
    }
}

export function permission() {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission; // "default" | "granted" | "denied"
}

/**
 * Synchronous prelude for the click handler: request permission and return the
 * resulting state. Must be invoked with no prior await to preserve user gesture.
 */
export function requestNotificationPermission() {
    if (!("Notification" in window)) {
        return Promise.resolve("unsupported");
    }
    if (Notification.permission === "granted") {
        return Promise.resolve("granted");
    }
    if (Notification.permission === "denied") {
        return Promise.resolve("denied");
    }
    // permission === "default": trigger native popup
    try {
        return Notification.requestPermission();
    } catch {
        // Legacy callback API — should not happen on modern browsers
        return new Promise((resolve) => {
            try {
                Notification.requestPermission((r) => resolve(r));
            } catch {
                resolve("denied");
            }
        });
    }
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
    return output;
}

async function getRegistration() {
    // Reuse the SW registered by index.html; if not there yet, register now.
    let reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) {
        reg = await navigator.serviceWorker.register(SW_PATH);
    }
    // Wait until the active worker is available (ready === has active worker).
    return await navigator.serviceWorker.ready;
}

function subToJSON(sub) {
    const json = sub.toJSON();
    return {
        endpoint: json.endpoint,
        keys: {
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
        },
    };
}

export async function getServerStatus() {
    if (!isPushSupported()) {
        return { configured: false, subscribed: false, hasBrowserSubscription: false, device_count: 0 };
    }
    let existing = null;
    try {
        const reg = await getRegistration();
        existing = await reg.pushManager.getSubscription();
    } catch {
        existing = null;
    }
    const endpoint = existing ? existing.endpoint : undefined;
    const { data } = await API.get("/notifications/status", {
        params: endpoint ? { endpoint } : {},
    });
    return { ...data, hasBrowserSubscription: Boolean(existing) };
}

/**
 * Complete the subscription flow. Assumes Notification.permission === "granted".
 * Safe to call multiple times — idempotent both client and server side.
 */
export async function registerPushSubscription() {
    if (!isPushSupported()) throw new Error("Il tuo browser non supporta le notifiche push.");
    if (Notification.permission !== "granted") {
        throw new Error("Permesso notifiche non concesso.");
    }

    const { data: keyResp } = await API.get("/notifications/vapid-public-key");
    const publicKey = keyResp.public_key;

    const reg = await getRegistration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
    }
    const body = subToJSON(sub);
    body.user_agent = (navigator.userAgent || "").slice(0, 280);
    await API.post("/notifications/subscribe", body);
    return true;
}

export async function unsubscribeAdmin() {
    if (!isPushSupported()) return true;
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        const endpoint = sub.endpoint;
        try { await sub.unsubscribe(); } catch { /* noop */ }
        try {
            await API.post("/notifications/unsubscribe", { endpoint });
        } catch { /* noop */ }
    }
    return true;
}

export async function sendTestPush() {
    const { data } = await API.post("/notifications/test");
    return data;
}
